/**
 * Row reducers for the CPT table's date cells ("Date From" / "Date To").
 * @module updateRowData/dateReducers
 */
import moment from "moment";
import { GeneralOrderDetails } from "../../../../../../data-model/medicalPaOrdersModel";
import { isFacilityExemptFromDateFromValidation } from "../../../../../../enums/organizationTypes";
import { CptCodeWithDetailsKeys } from "../../../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../../../utils/formatDate";
import {
  checkIfDateIsAfter,
  checkIfDateIsEarlierThanDateOfService,
  checkIfDateIsInCorrectFormat,
  checkIfDateIsLaterThanDateOfService,
} from "../computeErrorStates";
import { getAuthStatusFlags } from "./sharedHelpers";

/**
 * Reducer for edits to the "Date From" (start date) cell.
 *
 * Computes the start-date error against format and date-of-service rules,
 * but suppresses the error for facilities exempted by
 * {@link isFacilityExemptFromDateFromValidation} and for rows whose auth
 * status is not on-file / by-RISA. Also propagates a cross-field check to
 * the "Date To" cell: the end-date cell is flagged when the existing end
 * date is not strictly after the new start date.
 *
 * @param rowDataToUpdate - Existing row snapshot
 * @param updatedData - New start-date string entered by the user
 * @param generalOrderDetails - Order context used for date-of-service
 * @param category - Healthcare facility `category` driving the validation exemption
 * @returns New row object with updated StartFrom and EndAt cells
 */
export const updateRowDataForStartFrom = (
  rowDataToUpdate: any,
  updatedData: any,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  category?: string | null,
) => {
  const transformedDateOfService = formatDateToUTCWithFormat(
    generalOrderDetails?.payload?.date_of_service ?? "",
    "MM/DD/YYYY",
    moment.utc().format("MM/DD/YYYY"),
  );
  const hasError =
    !checkIfDateIsInCorrectFormat(updatedData, "MM/DD/YYYY") ||
    !checkIfDateIsEarlierThanDateOfService(
      updatedData,
      transformedDateOfService ?? "",
    );
  const endDate = rowDataToUpdate[CptCodeWithDetailsKeys.EndAt]?.data ?? "";
  const { isAuthOnFile, isAuthByRisa } = getAuthStatusFlags(rowDataToUpdate);
  const shouldShowDateFromError =
    (isAuthOnFile || isAuthByRisa) &&
    !isFacilityExemptFromDateFromValidation(category);
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.StartFrom]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.StartFrom],
      hasError: shouldShowDateFromError ? hasError : false,
      data: updatedData,
    },
    [CptCodeWithDetailsKeys.EndAt]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.EndAt],
      hasError: !checkIfDateIsAfter(endDate, updatedData),
      data: endDate,
    },
  };
};

/**
 * Reducer for edits to the "Date To" (end date) cell.
 *
 * The end date must be in `MM/DD/YYYY` format, on or after the order's
 * date of service, and strictly after the current "Date From" value. These
 * checks are surfaced for on-file and by-RISA rows.
 *
 * @param rowDataToUpdate - Existing row snapshot
 * @param updatedData - New end-date string entered by the user
 * @param generalOrderDetails - Order context used for date-of-service
 * @returns New row object with updated EndAt cell
 */
export const updateRowDataForEndAt = (
  rowDataToUpdate: any,
  updatedData: any,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
) => {
  const transformedDateOfService = formatDateToUTCWithFormat(
    generalOrderDetails?.payload?.date_of_service ?? "",
    "MM/DD/YYYY",
    moment.utc().format("MM/DD/YYYY"),
  );
  const fromDate =
    rowDataToUpdate[CptCodeWithDetailsKeys.StartFrom]?.data ?? "";
  const hasError =
    !checkIfDateIsInCorrectFormat(updatedData, "MM/DD/YYYY") ||
    !checkIfDateIsLaterThanDateOfService(
      updatedData,
      transformedDateOfService ?? "",
    ) ||
    !checkIfDateIsAfter(updatedData, fromDate);
  const { isAuthOnFile, isAuthByRisa } = getAuthStatusFlags(rowDataToUpdate);
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.EndAt]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.EndAt],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
  };
};
